/**
 * 渲染目标纹理池（M3-09）。
 *
 * `GpuBackend` 没有混合状态（backend.ts 的设计决策：合成必须靠采样背景纹理
 * 自己算），所以合成器每混合一层就要一块新的中间渲染目标（ping-pong）。
 * 逐帧、逐块地创建/销毁纹理在两个后端上都不便宜；池按 `${宽}x${高}` 分桶
 * 复用，用完 `release` 放回池里而不是销毁，下一块/下一帧直接摘。
 */
import type { GpuBackend, GpuTexture, TextureFormat } from '../backend';

export class GpuTexturePool {
  readonly #backend: GpuBackend;
  readonly #format: TextureFormat;
  readonly #free = new Map<string, GpuTexture[]>();
  readonly #all = new Set<GpuTexture>();

  constructor(backend: GpuBackend, format: TextureFormat) {
    this.#backend = backend;
    this.#format = format;
  }

  get format(): TextureFormat {
    return this.#format;
  }

  /** 总持有数（含正在使用与空闲的），供内存/泄漏检查用。 */
  get size(): number {
    return this.#all.size;
  }

  #key(width: number, height: number): string {
    return `${width}x${height}`;
  }

  /** 取一块可写渲染目标。优先复用空闲纹理，没有才新建。 */
  acquire(width: number, height: number, label?: string): GpuTexture {
    const key = this.#key(width, height);
    const bucket = this.#free.get(key);
    const reused = bucket?.pop();
    if (reused !== undefined) return reused;

    const texture = this.#backend.createTexture({
      width,
      height,
      format: this.#format,
      renderTarget: true,
      ...(label === undefined ? {} : { label }),
    });
    this.#all.add(texture);
    return texture;
  }

  /** 放回池里，供下次 acquire 复用。已销毁的纹理直接丢弃（不放回）。 */
  release(texture: GpuTexture): void {
    if (texture.destroyed) {
      this.#all.delete(texture);
      return;
    }
    const key = this.#key(texture.width, texture.height);
    const bucket = this.#free.get(key);
    if (bucket === undefined) this.#free.set(key, [texture]);
    else bucket.push(texture);
  }

  /** 上下文丢失恢复后整池作废：旧纹理引用已经指向失效资源，不能再复用。 */
  clear(): void {
    this.#free.clear();
    this.#all.clear();
  }

  destroy(): void {
    for (const texture of this.#all) {
      if (!texture.destroyed) texture.destroy();
    }
    this.clear();
  }
}
